import React, { useState } from "react";
import style from "../../../assets/scss/common/modal/addChildModal.module.scss";
import CloseBtn from "../../../assets/images/modal/close_btn.svg";
import RefundModal from "./RefundModal";
import { useTimeConverter } from "../../../hooks/hooks";
import {numberWithCommas} from "../../../util/commonFunctions";
import {useTranslation} from "react-i18next";
import {PaymentWay} from "../../../types/payment";

interface PaymentInfoModalProps {
  order: any;
  onClose: () => void;
}

export const PaymentInfoModal: React.FC<PaymentInfoModalProps> = ({ order, onClose }) => {
  const { t } = useTranslation("payment");
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const isKoreanProduct = order.paymentWay !== PaymentWay.PAYPAL
  const priceUnit = isKoreanProduct ? t('detail.modal.won') : t('detail.modal.dollar')

  const openRefundModal = () => setIsRefundModalOpen(true);
  const closeRefundModal = () => setIsRefundModalOpen(false);
  const { timeConverter } = useTimeConverter();
  const codeDiscountAmount = Math.max(order.affiliateCodeDiscountAmount, order.couponCodeDiscountAmount);

  const getStatusTextAndClass = (status: string, recallStatus: string) => {
    // 이용권 회수에 대한 조건처리
    if (recallStatus === "RECALL") {
      return {text: t('detail.row.recall'), className: style.cancelled};
    }else if (recallStatus === "PARTIALLY_RECALL") {
      if(status === "CANCELLED" ){
        return { text: t('detail.row.refund'), className: style.cancelled };
      } else if (status === "PARTIALLY_CANCELLED") {
        return {text: t('detail.row.partiallyCancel'), className: style.cancelled};
      }else {
        return {text: t('detail.row.partiallyRecall'), className: style.cancelled};
      }
    }

    switch (status) {
      case "AWAITING_PAYMENT":
        return { text: t('detail.row.awaitingPayment'), className: style.awaitingPayment };
      case "AWAITING_DEPOSIT":
        return { text: t('detail.row.checkVA'), className: style.awaitingDeposit };
      case "PAID":
        return { text: t('detail.row.orderComplete'), className: style.paid };
      case "FAIL":
        return { text: t('detail.row.orderFail'), className: style.fail };
      case "OUTSTANDING_PAYMENT":
        return { text: t('detail.row.cancel'), className: style.outstandingPayment };
      case "PARTIALLY_CANCELLED":
        return { text: t('detail.row.partiallyCancel'), className: style.partiallyCancelled };
      case "CANCELLED":
        return { text: t('detail.row.refund'), className: style.cancelled };
      default:
        return { text: "알수없음", className: "" };
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
        .writeText(text)
        .then(() => {
          alert("계좌번호가 클립보드에 복사되었습니다.");
        })
        .catch((err) => {
          console.error("클립보드에 복사하는 중 오류 발생:", err);
        });
  };

  // 환불 가능 여부를 확인하는 함수
  const isRefundable = () => {
    const paidDate = new Date(order.paidAt);
    const currentDate = new Date();
    const diffTime = currentDate.getTime() - paidDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  const { text: statusText, className: statusClass } = getStatusTextAndClass(order.status, order.recallStatus);

  return (
      <>
        <div className={style.addChildModalOverlay}>
          <div className={style.addChildInfoModal}>
            <div className={style.closeBtnContainer}>
              <img src={CloseBtn} alt="closeBtn" className={style.closeBtn} onClick={onClose} />
            </div>
            <div className={style.paymentInnerModalContainer}>
              <h3 className={style.addChildInfoTitle}>{t('detail.modal.title')}</h3>
              <div className={style.paymentTime}>
                <span className={style.timeText}>{timeConverter.convertToLocalDate(order.createAt)}</span>
                <span className={`${style.paymentResult} ${statusClass}`}>{statusText}</span>
              </div>
              <div className={style.innerTextPaymentContainer}>
                <h3 className={style.innerTextTitle}>{t('detail.modal.orderedProductInfo')}</h3>
                <div className={style.productContainer}>
                  <div>
                    <img
                        src={order.orderImage || "https://img1.newsis.com/2023/07/12/NISI20230712_0001313626_web.jpg"}
                        className={style.productImg} alt={"orderImage"}
                    />
                  </div>
                  <div className={style.productInfoContainer}>
                    <p className={style.productName}>{order.orderTitle}</p>
                    <p className={style.productCount}>{order.orderQuantity}{t('detail.modal.unit')}</p>
                    <p className={style.productPrice}>{order.regularPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{priceUnit}</p>
                  </div>
                </div>
              </div>
              <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                <h3 className={style.innerTextTitle}>{t('detail.modal.orderInfo')}</h3>
                <div className={style.amountContainer}>
                  <div className={style.totalAmount}>
                    <span>{t('detail.modal.totalProductPrice')}</span>
                    <span>{numberWithCommas(order.regularPriceAmount)}{priceUnit}</span>
                  </div>
                  <div className={style.discountAmount}>
                    <span>{t('detail.modal.discountPrice')}</span>
                    <span>
                    -{numberWithCommas(order.productDiscountAmount + order.couponDiscountAmount + codeDiscountAmount)}{priceUnit}
                  </span>
                  </div>
                  <div className={style.productDiscount}>
                    <span>ㄴ {t('detail.modal.productPrice')}</span>
                    <span>-{numberWithCommas(order.productDiscountAmount)}{priceUnit}</span>
                  </div>
                  <div className={style.couponDiscount}>
                    <span>ㄴ {t('detail.modal.couponPrice')}</span>
                    <span>-{numberWithCommas(order.couponDiscountAmount)}{priceUnit}</span>
                  </div>
                  <div className={style.codeDiscount}>
                    <span>ㄴ {t('detail.modal.codePrice')}</span>
                    <span>-{numberWithCommas(codeDiscountAmount)}{priceUnit}</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="400" height="2" viewBox="0 0 400 2" fill="none" className={style.lineImg}>
                    <path d="M0 1L400 0.999965" stroke="#898989" />
                  </svg>
                  <div className={style.totalPaymentAmount}>
                    <span>{t('detail.modal.totalOrderPrice')}</span>
                    <span>{numberWithCommas(order.payAmount)}{priceUnit}</span>
                  </div>
                </div>
              </div>
              {order.paymentWay === "CARD" && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <div className={style.paymentMethodTextContainer}>
                      <h3 className={style.innerTextTitle}>{t('detail.modal.paymentMethod')}</h3>
                      <span className={style.paymentMethod}>{t('detail.modal.creditCard')}</span>
                    </div>
                  </div>
              )}
              {order.paymentWay === "GOOGLE" && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <div className={style.paymentMethodTextContainer}>
                      <h3 className={style.innerTextTitle}>{t('detail.modal.paymentMethod')}</h3>
                      <span className={style.paymentMethod}>Google Play</span>
                    </div>
                  </div>
              )}
              {order.paymentWay === "APPLE" && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <div className={style.paymentMethodTextContainer}>
                      <h3 className={style.innerTextTitle}>{t('detail.modal.paymentMethod')}</h3>
                      <span className={style.paymentMethod}>App Store</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="2" viewBox="0 0 400 2" fill="none" className={style.lineImg}>
                      <path d="M0 1H400" stroke="#C6C6C6" stroke-dasharray="2 2" />
                    </svg>
                    <ul className={style.paymentDescContainer}>
                      <li>App Store 환불은 Apple을 통해서만 가능합니다.</li>
                      <li>Apple 고객센터(080-333-4000)로 문의하시기 바랍니다.</li>
                    </ul>
                  </div>
              )}
              {order.paymentWay === "PAYPAL" && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <div className={style.paymentMethodTextContainer}>
                      <h3 className={style.innerTextTitle}>{t('detail.modal.paymentMethod')}</h3>
                      <span className={style.paymentMethod}>PayPal</span>
                    </div>
                  </div>
              )}
              {order.paymentWay === "VBANK" && order.status === "OUTSTANDING_PAYMENT" && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <div className={style.paymentMethodTextContainer}>
                      <h3 className={style.innerTextTitle}>{t('detail.modal.orderCancel')}</h3>
                      <span className={style.paymentMethod}>{t('detail.modal.va')}</span>
                    </div>
                    <div className={style.paymentMethodTextContainer}>
                  <span className={style.paymentDescription}>
                    {t('detail.modal.cancelDesc1')}
                  </span>
                    </div>
                  </div>
              )}
              {order.paymentWay === "VBANK" && order.status !== "OUTSTANDING_PAYMENT" && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <div className={style.paymentMethodTextContainer}>
                      <h3 className={style.innerTextTitle}>{t('detail.modal.paymentMethod')}</h3>
                      <span className={style.paymentMethod}>{t('detail.modal.va')}</span>
                    </div>
                    {order.status !== "PAID" && (
                        <>
                          <div className={style.paymentMethodTextContainer}>
                      <span className={style.paymentDescription}>
                        {t('detail.modal.vaDesc1')}<br />
                        {t('detail.modal.vaDesc2')}<br />
                        {t('detail.modal.vaDesc3')}
                      </span>
                          </div>
                          <div className={`${style.paymentMethodTextContainer} ${style.vBankTextContainer}`}>
                            <span className={style.vBankText}>{`${order.vbankName} ${order.vbankAccountNumber}`}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="25"
                                height="24"
                                viewBox="0 0 25 24"
                                fill="none"
                                onClick={() => copyToClipboard(order.vbankAccountNumber)}
                                style={{ cursor: "pointer" }}
                            >
                              <path
                                  d="M9.69141 9.73204V8.082C9.69141 7.4869 10.1783 7 10.7734 7H16.2645C16.8596 7 17.3465 7.4869 17.3465 8.082V13.5731C17.3465 14.1682 16.8596 14.6551 16.2645 14.6551H15.1555"
                                  stroke="#757575"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                              />
                              <path
                                  d="M14.0731 9.73242H8.582C7.98443 9.73242 7.5 10.2168 7.5 10.8144V16.3056C7.5 16.9031 7.98443 17.3875 8.582 17.3875H14.0731C14.6707 17.3875 15.1551 16.9031 15.1551 16.3056V10.8144C15.1551 10.2168 14.6707 9.73242 14.0731 9.73242Z"
                                  stroke="#757575"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                              />
                            </svg>
                          </div>

                          <svg xmlns="http://www.w3.org/2000/svg" width="400" height="2" viewBox="0 0 400 2" fill="none" className={style.lineImg}>
                            <path d="M0 1H400" stroke="#C6C6C6" stroke-dasharray="2 2" />
                          </svg>
                          <ul className={style.paymentDescContainer}>
                            <li>{t('detail.modal.vaDesc3')}</li>
                            <li>{t('detail.modal.vaDesc4')}</li>
                            <li>{t('detail.modal.vaDesc5')}</li>
                            <li className={style.noDisc}>- {t('detail.modal.vaSubDesc1')}</li>
                            <li className={style.noDisc}>- {t('detail.modal.vaSubDesc2')}</li>
                          </ul>
                        </>
                    )}
                  </div>
              )}
              {/*{order.status === "CANCELLED" && (*/}
              {(order.status === "CANCELLED" || order.status === "PARTIALLY_CANCELLED") && (
                  <div className={style.innerTextPaymentContainer} style={{ marginTop: "30px" }}>
                    <h3 className={style.innerTextTitle}>{t('detail.modal.refund.refundInfo')}</h3>
                    <div className={style.amountContainer}>
                      {Array.isArray(order.refundDetails) && order.refundDetails.length > 0 ? (
                          order.refundDetails.map((refund: any, index: number) => (
                              <div key={refund.idx || index}>
                                <div className={style.totalAmount}>
                          <span>
                            {timeConverter.convertToFormattedDate(timeConverter.convertToLocalDate(refund.createAt))}{" "}
                            {order.refundPrice < order.payAmount ? t('detail.modal.refund.partialRefund') : ""}
                          </span>
                                  <span>{refund?.refundPrice?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{priceUnit}</span>
                                </div>
                                {index < order.refundDetails.length - 1 && (
                                    <div style={{ marginBottom: "10px" }} />
                                )}
                              </div>
                          ))
                      ) : (
                          <div className={style.totalAmount}>
                      <span>
                        {timeConverter.convertToFormattedDate(timeConverter.convertToLocalDate(order.createAt))}{" "}
                        {order.refundPrice < order.payAmount ? t('detail.modal.refund.partialRefund') : ""}
                      </span>
                            <span>{order?.refundPrice?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{priceUnit}</span>
                          </div>
                      )}

                      <svg xmlns="http://www.w3.org/2000/svg" width="400" height="2" viewBox="0 0 400 2" fill="none" className={style.lineImg}>
                        <path d="M0 1L400 0.999965" stroke="#898989" />
                      </svg>
                      <div className={style.totalPaymentAmount} style={{color: "#E60063"}}>
                        <span>{t('detail.modal.refund.totalRefundPrice')}</span>
                        <span>{
                          Array.isArray(order.refundDetails) && order.refundDetails.length > 0
                              ? order.refundDetails.reduce((total: number, refund: any) => total + (refund.refundPrice || 0), 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              : order?.refundPrice?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0'
                        }{priceUnit}</span>
                      </div>
                    </div>
                  </div>
              )}
              {order.status !== "APPLE" && order.status === "PAID" && isRefundable() && (
                  <button className={style.submitBtn} onClick={openRefundModal}>
                    {t('detail.modal.refund.title')}
                  </button>
              )}
            </div>
          </div>
        </div>
        <RefundModal isOpen={isRefundModalOpen} onClose={closeRefundModal}/>
      </>
  );
};
